import { Add, Close, Save } from '@mui/icons-material';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Theme,
  Typography,
  Box,
  ButtonGroup,
  Button,
  Divider
} from '@mui/material';
import { useQuery } from 'react-query';
import { deepEqual } from 'fast-equals';
import { Form, Formik } from 'formik';
import { useHistory, useParams, Prompt } from 'react-router-dom';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import makeStyles from '@mui/styles/makeStyles';
import { Field } from 'formik';
import React, { FC, useState, useMemo } from 'react';
import {
  getMuniciplaitySchoolDistricts,
  putNeighborhoodGeneralInfo,
  getStartSheetTemplates,
  getMunicipalities,
  getSitePrepBudget,
  createNeighborhoodGeneralInfo,
  getNeighborhoodBuildTypes,
  getAvailableProjectManagers,
  getAvailableSupervisors
} from '../../../../fetch';
import { formatMoney, decimalRegExp } from '../../../../helpers';
import {
  IMunicipality,
  ISchoolDistrict,
  IStartSheetTemplate,
  IDropdownResponse,
  INeighborhoodDetailGeneralInfo,
  IIMunicipalitiesResponse,
  IStartSheetTemplateResponse,
  INeighborhoodDetailGeneralInfoPut
} from '../../../../models';
import { Loader, SMBSelect } from '../../../../components';

interface IGeneralInformation {
  generalInfo: INeighborhoodDetailGeneralInfo;
  isLoadingGeneralInfo: boolean;
  refetchGeneralInfo: () => void;
}


const GeneralInfoSchema = Yup.object().shape({
  neighborhoodId: Yup.string(),
  isActive: Yup.boolean(),
  name: Yup.string().when(['neighborhoodId'], {
    is: (neighborhoodId, selectedPage) => {
      return neighborhoodId === 'add';
    },
    then: Yup.string().max(100, 'Max 100 characters').required('Required'),
    otherwise: Yup.string().max(100, 'Max 100 characters').notRequired()
  }),
  allowPriceSheet: Yup.boolean(),
  startSheetTemplateId: Yup.number().nullable(),
  assignChecklistTo: Yup.string(),
  phase: Yup.string().max(10, 'Max 10 characters').required(),
  code: Yup.number().when(['neighborhoodId'], {
    is: neighborhoodId => {
      return neighborhoodId === 'add';
    },
    then: Yup.number().typeError('Please enter a number').required('Required'),
    otherwise: Yup.number().typeError('Please enter a number').required('Required')
  }),
  sageGeneralJobCode: Yup.number().typeError('Please enter a number'),
  sageAmenityEntranceJobCode: Yup.number().typeError('Please enter a number'),
  narrative: Yup.string().required(),
  showPricePerSquareFoot: Yup.boolean(),
  municipalityId: Yup.number().nullable(),
  municipalitySchoolDistrictId: Yup.number().nullable(),
  isStoneMartinDevelopment: Yup.boolean().nullable(),
  developerName: Yup.string().when(['neighborhoodId', 'isStoneMartinDevelopment'], {
    is: isStoneMartinDevelopment => {
      return isStoneMartinDevelopment === false;
    },
    then: Yup.string().required('Required'),
    otherwise: Yup.string().notRequired()
  }),
  isStoneMartinErosionController: Yup.boolean()
    .nullable()
    .when(['neighborhoodId', 'isStoneMartinDevelopment'], {
      is: (neighborhoodId, isStoneMartinDevelopment) => {
        return neighborhoodId === 'add' && isStoneMartinDevelopment === true;
      },
      then: Yup.boolean().required('Responsible for Erosion Control name is required'),
      otherwise: Yup.boolean().notRequired()
    }),
  isTrueUp: Yup.boolean()
    .nullable()
    .when(['neighborhoodId', 'isStoneMartinDevelopment'], {
      is: (neighborhoodId, isStoneMartinDevelopment) => {
        return neighborhoodId === 'add' && isStoneMartinDevelopment === true;
      },
      then: Yup.boolean().required('Contract is a True Up is required'),
      otherwise: Yup.boolean().notRequired()
    }),
  trueUpInitialPayment: Yup.string()
    .nullable()
    .when(['neighborhoodId', 'isTrueUp'], {
      is: (neighborhoodId, isTrueUp) => {
        return neighborhoodId === 'add' && isTrueUp === true;
      },
      then: Yup.string().required('True Up Initial Payment is required'),
      otherwise: Yup.string().notRequired()
    }),
  trueUpType: Yup.string().when(['neighborhoodId', 'contractIsTrueUp'], {
    is: (neighborhoodId, contractIsTrueUp) => {
      return neighborhoodId === 'add' && contractIsTrueUp === true;
    },
    then: Yup.string().required('True Up Type is required'),
    otherwise: Yup.string().notRequired()
  }),
  trueUpPercentage: Yup.number()
    .nullable()
    .when(['neighborhoodId', 'trueUpType'], {
      is: (neighborhoodId, trueUpType) => {
        return neighborhoodId === 'add' && trueUpType === 'Percent';
      },
      then: Yup.number().required('True Up Percent is required'),
      otherwise: Yup.number().notRequired()
    }),
  titleTransferFees: Yup.string().required('Title Transfer Fee is required'),
  platBook: Yup.number().typeError('Please enter a number').nullable(),
  page: Yup.number().typeError('Please enter a number').nullable(),
  sitePrepBudgetLevel: Yup.string().required('Required'),
  buildTypes: Yup.array(),
  startsPerWeek: Yup.number().nullable(),
  documentationTimeInDays: Yup.number().nullable().typeError('Please enter a number'),
  buildTimeInDays: Yup.number().nullable().typeError('Please enter a number')
});

export const GeneralInformation: FC<IGeneralInformation> = ({
  generalInfo,
  isLoadingGeneralInfo,
  refetchGeneralInfo
}) => {
  const classes = useStyles();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const { neighborhoodId }: { neighborhoodId: string } = useParams();
  const [municipalityId, setMunicipalityId] = useState<string | number | null>(null);
  const { isLoading: isLoadingMunicipalities, data: municipalitiesData } = useQuery<
    IIMunicipalitiesResponse,
    Error
  >(['getMunicipalities'], () => getMunicipalities());

  const municipalities = useMemo(() => municipalitiesData?.records || [], [municipalitiesData]);

  const { isLoading: isLoadingStartSheetTemplates, data: startSheetTemplatesResponse } = useQuery<
    IStartSheetTemplateResponse,
    Error
  >(['getStartSheetTemplates'], () => getStartSheetTemplates('Neighborhood'));

  const startSheetTemplates = useMemo(
    () => startSheetTemplatesResponse?.records || [],
    [startSheetTemplatesResponse]
  );

  const { isLoading: isLoadingSitePrepBudgets, data: sitePrepBudgets } = useQuery<
    IDropdownResponse[],
    Error
  >(['getSitePrepBudget'], () => getSitePrepBudget());

  const { isLoading: isLoadingBuildTypes, data: buildTypes } = useQuery<IDropdownResponse[], Error>(
    ['getBuildTypes'],
    () => getNeighborhoodBuildTypes()
  );

  const { isLoading: isLoadingAvailableProjectManagers, data: availableProjectManagers } = useQuery<
    IDropdownResponse[],
    Error
  >(['getAvailableProjectManagers'], () => getAvailableProjectManagers());

  const { isLoading: isLoadingAvailableSupervisors, data: availableSupervisors } = useQuery<
    IDropdownResponse[],
    Error
  >(['getAvailableSupervisors'], () => getAvailableSupervisors());

  let assignees = [];
  if (availableProjectManagers && availableSupervisors) {
    let combinedList = [...availableProjectManagers, ...availableSupervisors];
    assignees = Array.from(new Set(combinedList.map(obj => obj.description))).map(name =>
      combinedList.find(obj => obj.description === name)
    );
  }

  const { isLoading: isLoadingDistricts, data: schoolDistricts } = useQuery<
    ISchoolDistrict[],
    Error
  >(
    ['municiplaity-districts', municipalityId],
    () => getMuniciplaitySchoolDistricts(municipalityId),
    {
      enabled: !!municipalityId
    }
  );

  return (
    <>
      {isLoadingGeneralInfo && (
        <Box mt={1} minHeight="30rem" display="flex" flexDirection="column" justifyContent="center">
          <Loader type="inline" position="centered">
            Loading...
          </Loader>
        </Box>
      )}
      {!isLoadingGeneralInfo && (
        <Formik
          enableReinitialize={true}
          initialValues={{
            isActive: generalInfo?.isActive ?? true,
            name: generalInfo?.name ?? '',
            allowPriceSheet: generalInfo?.allowPriceSheet === false ? false : true,
            startSheetTemplateId:
              generalInfo && generalInfo.startSheetTemplateId
                ? generalInfo.checklist?.checklistId
                  ? generalInfo.checklist?.checklistId
                  : generalInfo.startSheetTemplateId
                : '',
            assignChecklistTo: generalInfo?.assignChecklistTo ?? '',
            phase: generalInfo?.phase ?? '',
            code: generalInfo?.code ?? '',
            sageGeneralJobCode: generalInfo?.sageGeneralJobCode ?? '',
            sageAmenityEntranceJobCode: generalInfo?.sageAmenityEntranceJobCode ?? '',
            narrative: generalInfo?.narrative ?? '',
            showPricePerSquareFoot: generalInfo?.showPricePerSquareFoot === false ? false : true,
            municipalityId: generalInfo?.municipalityId ?? '',
            municipalitySchoolDistrictId: generalInfo?.municipalitySchoolDistrictId ?? '',
            isStoneMartinDevelopment: generalInfo?.isStoneMartinDevelopment ?? true,
            developerName: generalInfo?.developerName ?? '',
            isStoneMartinErosionController: generalInfo?.isStoneMartinErosionController ?? true,
            isTrueUp: generalInfo?.isTrueUp ?? true,
            trueUpInitialPayment: generalInfo?.trueUpInitialPayment
              ? formatMoney(generalInfo?.trueUpInitialPayment)
              : '',
            trueUpType: generalInfo?.trueUpType ?? 'Fixed Price',
            trueUpPercentage: generalInfo?.trueUpPercentage
              ? `${generalInfo?.trueUpPercentage}%`
              : '',
            titleTransferFees: !isNaN(generalInfo?.titleTransferFees)
              ? formatMoney(generalInfo?.titleTransferFees)
              : '',
            platBook: generalInfo?.platBook ?? '',
            page: generalInfo?.page ?? '',
            sitePrepBudgetLevel: generalInfo?.sitePrepBudgetLevel ?? 'Level1',
            buildTypes: generalInfo?.buildTypes ?? [],
            startsPerWeek: generalInfo?.startsPerWeek ?? 0,
            documentationTimeInDays: generalInfo?.documentationTimeInDays ?? 1,
            buildTimeInDays: generalInfo?.buildTimeInDays ?? 1
          }}
          validationSchema={GeneralInfoSchema}
          validateOnMount={true}
          onSubmit={async (values, actions) => {
            try {
              const data: INeighborhoodDetailGeneralInfoPut = {
                ...values,
                code: values?.code ? Number(values.code) : null,
                sageGeneralJobCode: values?.sageGeneralJobCode
                  ? Number(values.sageGeneralJobCode)
                  : null,
                sageAmenityEntranceJobCode: values?.sageAmenityEntranceJobCode
                ? Number(values.sageAmenityEntranceJobCode)
                : null,
                municipalityId: values?.municipalityId ? Number(values.municipalityId) : null,
                municipalitySchoolDistrictId: values?.municipalitySchoolDistrictId
                  ? Number(values.municipalitySchoolDistrictId)
                  : null,
                page: values?.page ? Number(values.page) : null,
                titleTransferFees: values?.titleTransferFees
                  ? Number(
                      (values.titleTransferFees as string).replace('$', '').replaceAll(',', '')
                    )
                  : 0,
                trueUpInitialPayment: values?.trueUpInitialPayment
                  ? Number(
                      (values.trueUpInitialPayment as unknown as string)
                        .replace('$', '')
                        .replaceAll(',', '')
                    )
                  : 0,
                trueUpPercentage: values?.trueUpPercentage
                  ? Number((values.trueUpPercentage as unknown as string).replace('%', ''))
                  : 0,
                platBook: values?.platBook ? Number(values.platBook) : null,
                startSheetTemplateId: values?.startSheetTemplateId
                  ? Number(values.startSheetTemplateId)
                  : null,
                sitePrepBudgetLevel: values?.sitePrepBudgetLevel
                  ? values.sitePrepBudgetLevel
                  : 'Level1'
              };
              if (neighborhoodId !== 'add') {
                await putNeighborhoodGeneralInfo(parseInt(neighborhoodId), data);
                await refetchGeneralInfo();
                enqueueSnackbar('Neighborhood has been updated successfully!', {
                  variant: 'success'
                });
              }
              if (neighborhoodId === 'add') {
                const neighborhoodId = await createNeighborhoodGeneralInfo(data);
                actions.resetForm();
                enqueueSnackbar('Neighborhood has been updated successfully!', {
                  variant: 'success'
                });

                history.push(`/development/neighborhoods/${neighborhoodId}/general-information`);
              }
            } catch (error) {
              enqueueSnackbar(
                error?.Detail ?? 'Something went wrong. Neighborhood was not updated.',
                {
                  variant: 'error'
                }
              );
            }
          }}
        >
          {({
            isSubmitting,
            values,
            initialValues,
            setFieldValue,
            errors,
            touched,
            handleSubmit,
            dirty,
            isValid,
            handleBlur
          }) => {
            return (
              <>
                {isSubmitting && (
                  <Loader type="fullscreen" position="centered">
                    Saving...
                  </Loader>
                )}
                <Prompt
                  when={!deepEqual(initialValues, values)}
                  message="You have unsaved changes, are you sure you want to exit?"
                />
                <Form onSubmit={handleSubmit}>
                  <Box mt={1}>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Switch
                              color="primary"
                              checked={values.isActive}
                              id="active-inactive"
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                setFieldValue('isActive', e.target.checked);
                              }}
                            />
                          }
                          label="Active"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Grid container alignItems="center" justifyContent="center" spacing={1}>
                          <Grid item xs={12} sm={12}>
                            <Field name={`name`}>
                              {({ field }) => {
                                return (
                                  <TextField
                                    fullWidth
                                    required={neighborhoodId === 'add'}
                                    variant="outlined"
                                    autoComplete="nope"
                                    label="Neighborhood Name"
                                    name="name"
                                    size="small"
                                    value={values.name}
                                    onBlur={handleBlur}
                                    onChange={e => setFieldValue('name', e.target.value)}
                                    error={Boolean(touched.name && errors.name)}
                                    helperText={touched.name && errors.name}
                                  />
                                );
                              }}
                            </Field>
                          </Grid>
                          <Grid item xs={12} sm={12}>
                            <Field name={`allowPriceSheet`}>
                              {({ field }) => {
                                return (
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        color="primary"
                                        checked={values.allowPriceSheet}
                                        id="active-inactive"
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                          setFieldValue('allowPriceSheet', !values.allowPriceSheet);
                                        }}
                                      />
                                    }
                                    label="Allow Price Sheet"
                                  />
                                );
                              }}
                            </Field>
                          </Grid>
                          <Grid item xs={12}>
                            {(generalInfo === null || !generalInfo?.checklist) && (
                              <SMBSelect
                                onChange={e => {
                                  setFieldValue('startSheetTemplateId', e.target.value);
                                }}
                                name={`startSheetTemplateId`}
                                label={'Neighborhood Start Sheet Checklist Template'}
                                id={`startSheetTemplateId`}
                                variant="outlined"
                                handleClear={() => setFieldValue('startSheetTemplateId', '')}
                                value={values.startSheetTemplateId}
                                onBlur={handleBlur}
                                disabled={isLoadingStartSheetTemplates}
                                error={errors && errors.startSheetTemplateId ? true : false}
                              >
                                <MenuItem key={`${0}`} value={null}>
                                  Do not create checklist
                                </MenuItem>
                                {startSheetTemplates &&
                                  startSheetTemplates.map(
                                    (template: IStartSheetTemplate, index) => {
                                      return (
                                        <MenuItem
                                          key={`${index + 1}`}
                                          value={template.startSheetTemplateId}
                                        >
                                          {template.startSheetTemplateId} - {template.name}
                                        </MenuItem>
                                      );
                                    }
                                  )}
                              </SMBSelect>
                            )}
                            {generalInfo && generalInfo?.checklist && (
                              <Grid container spacing={1}>
                                <Grid item xs={12}>
                                  <Typography variant="body1">
                                    Neighborhood Start Sheet Checklist Template
                                  </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography variant="body2">
                                    <span style={{ fontWeight: 'bold' }}>Created by: </span>
                                    {generalInfo?.checklist?.createdBy ?? 'Joe Cool'}
                                  </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography variant="body2">
                                    <span style={{ fontWeight: 'bold' }}>Assigned to: </span>
                                    {generalInfo?.checklist?.assignedTo ?? 'Me'}
                                  </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography variant="body2">
                                    <span style={{ fontWeight: 'bold' }}>Completed On: </span>
                                    {generalInfo?.checklist?.completedOn ?? ''}
                                  </Typography>
                                </Grid>
                              </Grid>
                            )}
                          </Grid>
                          {generalInfo && !generalInfo.checklist && (
                            <Grid item xs={12} sm={12}>
                              <SMBSelect
                                onChange={e => {
                                  console.log(e.target);
                                  setFieldValue('assignChecklistTo', e.target.value);
                                }}
                                handleClear={() => setFieldValue('assignChecklistTo', '')}
                                variant="outlined"
                                name={`assignChecklistTo`}
                                label={`Assign Checklist To`}
                                id={`assignChecklistTo`}
                                value={values.assignChecklistTo}
                                onBlur={handleBlur}
                                disabled={
                                  isLoadingAvailableSupervisors || isLoadingAvailableProjectManagers
                                }
                                error={errors && errors.assignChecklistTo ? true : false}
                              >
                                <MenuItem key={`${0}`} value={''}>
                                  Unassigned
                                </MenuItem>
                                {assignees &&
                                  assignees.map((people, index) => {
                                    return (
                                      <MenuItem key={`${index + 1}`} value={people.description}>
                                        {people.description}
                                      </MenuItem>
                                    );
                                  })}
                              </SMBSelect>
                            </Grid>
                          )}
                          <Grid item xs={12} sm={12}>
                            <Field name={`phase`}>
                              {({ field }) => {
                                return (
                                  <TextField
                                    required
                                    fullWidth
                                    variant="outlined"
                                    autoComplete="nope"
                                    label="Phase"
                                    name="phase"
                                    size="small"
                                    value={values.phase}
                                    onBlur={handleBlur}
                                    onChange={e => setFieldValue('phase', e.target.value)}
                                    error={Boolean(touched.phase && errors.phase)}
                                    helperText={touched.phase && errors.phase}
                                  />
                                );
                              }}
                            </Field>
                          </Grid>
                          <Grid item xs={12} sm={12}>
                            <TextField
                              required
                              fullWidth
                              variant="outlined"
                              autoComplete="nope"
                              label="Code"
                              name="code"
                              size="small"
                              value={values.code}
                              onBlur={handleBlur}
                              onChange={e => setFieldValue('code', e.target.value)}
                              error={Boolean(touched.code && errors.code)}
                              helperText={touched.code && errors.code}
                            />
                          </Grid>
                          <Grid item xs={12} sm={12}>
                            <TextField
                              fullWidth
                              variant="outlined"
                              autoComplete="nope"
                              label="Sage General Job Code"
                              name="sageGeneralJobCode"
                              size="small"
                              InputLabelProps={{
                                shrink: values.sageGeneralJobCode ? true : false
                              }}
                              value={values.sageGeneralJobCode}
                              onBlur={handleBlur}
                              onChange={e => setFieldValue('sageGeneralJobCode', e.target.value)}
                              error={Boolean(
                                touched.sageGeneralJobCode && errors.sageGeneralJobCode
                              )}
                              helperText={touched.sageGeneralJobCode && errors.sageGeneralJobCode}
                            />
                          </Grid>
                          <Grid item xs={12} sm={12}>
                            <TextField
                              fullWidth
                              variant="outlined"
                              autoComplete="nope"
                              label="Sage Amenity And Entrance Job Code"
                              name="sageAmenityEntranceJobCode"
                              size="small"
                              InputLabelProps={{
                                shrink: values.sageAmenityEntranceJobCode ? true : false
                              }}
                              value={values.sageAmenityEntranceJobCode}
                              onBlur={handleBlur}
                              onChange={e => setFieldValue('sageAmenityEntranceJobCode', e.target.value)}
                              error={Boolean(
                                touched.sageAmenityEntranceJobCode && errors.sageAmenityEntranceJobCode
                              )}
                              helperText={touched.sageAmenityEntranceJobCode && errors.sageAmenityEntranceJobCode}
                            />
                          </Grid>
                          <Grid item xs={12} sm={12}>
                            <TextField
                              required
                              fullWidth
                              multiline
                              minRows={6}
                              variant="outlined"
                              autoComplete="nope"
                              label="Neighborhood Narrative"
                              name="Neighborhood Narrative"
                              size="small"
                              value={values.narrative}
                              onBlur={handleBlur}
                              onChange={e => setFieldValue('narrative', e.target.value)}
                              error={Boolean(errors.narrative)}
                              helperText={errors.narrative}
                            />
                          </Grid>
                          <Grid item xs={12} sm={12}>
                            <Field name={`showPricePerSquareFoot`}>
                              {({ field }) => {
                                return (
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        color="primary"
                                        checked={values.showPricePerSquareFoot}
                                        id="active-inactive"
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                          setFieldValue(
                                            'showPricePerSquareFoot',
                                            !values.showPricePerSquareFoot
                                          );
                                        }}
                                      />
                                    }
                                    label="Show Price Per Sq Ft"
                                  />
                                );
                              }}
                            </Field>
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Grid container alignItems="center" justifyContent="center" spacing={1}>
                          <Grid item xs={12} sm={12}>
                            <SMBSelect
                              onChange={e => {
                                setFieldValue('municipalityId', e.target.value);
                                setMunicipalityId(e.target.value);
                              }}
                              handleClear={() => {
                                setFieldValue('municipalityId', '');
                                setMunicipalityId('');
                              }}
                              name={`municipalityId`}
                              label={`Municipality`}
                              id={`municipalityId`}
                              variant="outlined"
                              value={values.municipalityId}
                              onBlur={handleBlur}
                              disabled={isLoadingMunicipalities}
                              error={Boolean(touched.municipalityId && errors.municipalityId)}
                              helperText={touched.municipalityId && errors.municipalityId}
                            >
                              {municipalities?.map((municipality: IMunicipality, index) => {
                                return (
                                  <MenuItem key={`${index}`} value={municipality.municipalityId}>
                                    {municipality.municipalityId} - {municipality.name}
                                  </MenuItem>
                                );
                              })}
                            </SMBSelect>
                          </Grid>
                          {schoolDistricts?.length > 0 && (
                            <Grid item xs={12} sm={12}>
                              <SMBSelect
                                onChange={e => {
                                  setFieldValue('municipalitySchoolDistrictId', e.target.value);
                                }}
                                variant="outlined"
                                handleClear={() =>
                                  setFieldValue('municipalitySchoolDistrictId', '')
                                }
                                name={`municipalitySchoolDistrictId`}
                                label={`Munic. School Dist.`}
                                id={`municipalitySchoolDistrictId`}
                                size="small"
                                value={values.municipalitySchoolDistrictId}
                                onBlur={handleBlur}
                                disabled={isLoadingDistricts || !values.municipalityId}
                                error={Boolean(
                                  touched.municipalitySchoolDistrictId &&
                                    errors.municipalitySchoolDistrictId
                                )}
                                helperText={
                                  touched.municipalitySchoolDistrictId &&
                                  errors.municipalitySchoolDistrictId
                                }
                              >
                                {schoolDistricts &&
                                  schoolDistricts.map((schoolDistrict: ISchoolDistrict, index) => {
                                    return (
                                      <MenuItem
                                        key={`${index + 1}`}
                                        value={schoolDistrict.schoolDistrictId}
                                      >
                                        {schoolDistrict.schoolDistrictId} -{' '}
                                        {schoolDistrict.schoolDistrictName}
                                      </MenuItem>
                                    );
                                  })}
                              </SMBSelect>
                            </Grid>
                          )}
                          <Grid item xs={12} sm={12}>
                            <Field name={`isStoneMartinDevelopment`}>
                              {({ field }) => {
                                return (
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        color="primary"
                                        checked={values.isStoneMartinDevelopment}
                                        id="active-inactive"
                                        onChange={(
                                          e: React.ChangeEvent<HTMLInputElement>,
                                          checked
                                        ) => {
                                          setFieldValue('isStoneMartinDevelopment', checked);
                                          if (checked) {
                                            setFieldValue('developerName', '');
                                            setFieldValue('isStoneMartinErosionController', true);
                                            setFieldValue('isTrueUp', true);
                                            setFieldValue('trueUpInitialPayment', 0);
                                            setFieldValue('trueUpType', 'Fixed Price');
                                            setFieldValue('trueUpPercentage', 0);
                                          }
                                        }}
                                      />
                                    }
                                    label="Developer is Stone Martin"
                                  />
                                );
                              }}
                            </Field>
                          </Grid>
                          {!values.isStoneMartinDevelopment && (
                            <>
                              <Grid item xs={12} sm={12}>
                                <Field name={`developerName`}>
                                  {({ field }) => {
                                    return (
                                      <TextField
                                        fullWidth
                                        multiline
                                        variant="outlined"
                                        autoComplete="nope"
                                        label="Developer Name"
                                        name="Developer Name"
                                        size="small"
                                        value={values.developerName}
                                        onBlur={handleBlur}
                                        onChange={e =>
                                          setFieldValue('developerName', e.target.value)
                                        }
                                        error={Boolean(
                                          touched.developerName && errors.developerName
                                        )}
                                        helperText={touched.developerName && errors.developerName}
                                      />
                                    );
                                  }}
                                </Field>
                              </Grid>
                              <Grid item xs={12} sm={12}>
                                <Field name={`isStoneMartinErosionController`}>
                                  {({ field }) => {
                                    return (
                                      <FormControlLabel
                                        control={
                                          <Switch
                                            color="primary"
                                            checked={values.isStoneMartinErosionController}
                                            id="active-inactive"
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                              setFieldValue(
                                                'isStoneMartinErosionController',
                                                !values.isStoneMartinErosionController
                                              );
                                            }}
                                          />
                                        }
                                        label="Stone Martin is Responsible for Erosion Control"
                                      />
                                    );
                                  }}
                                </Field>
                              </Grid>
                              <Grid item xs={12} sm={12}>
                                <Field name={`isTrueUp`}>
                                  {({ field }) => {
                                    return (
                                      <FormControlLabel
                                        control={
                                          <Switch
                                            color="primary"
                                            checked={values.isTrueUp}
                                            id="active-inactive"
                                            onChange={(
                                              e: React.ChangeEvent<HTMLInputElement>,
                                              checked
                                            ) => {
                                              setFieldValue('isTrueUp', checked);
                                              if (!checked) {
                                                setFieldValue('trueUpInitialPayment', 0);
                                                setFieldValue('trueUpType', 'Fixed Price');
                                              }
                                            }}
                                          />
                                        }
                                        label="Contract is a True Up"
                                      />
                                    );
                                  }}
                                </Field>
                              </Grid>

                              {values.isTrueUp && (
                                <>
                                  <Grid item xs={12} sm={12}>
                                    <Field name={`trueUpInitialPayment`}>
                                      {({ field }) => {
                                        return (
                                          <TextField
                                            fullWidth
                                            variant="outlined"
                                            autoComplete="nope"
                                            label="True Up initial payment amount"
                                            name="trueUpInitialPayment"
                                            size="small"
                                            value={values.trueUpInitialPayment}
                                            onBlur={e => {
                                              handleBlur(e);
                                              setFieldValue(
                                                'trueUpInitialPayment',
                                                formatMoney(e.target.value, 2)
                                              );
                                            }}
                                            onChange={e =>
                                              setFieldValue('trueUpInitialPayment', e.target.value)
                                            }
                                            error={Boolean(
                                              touched.trueUpInitialPayment &&
                                                errors.trueUpInitialPayment
                                            )}
                                            helperText={
                                              touched.trueUpInitialPayment &&
                                              errors.trueUpInitialPayment
                                            }
                                          />
                                        );
                                      }}
                                    </Field>
                                  </Grid>
                                  <Grid item xs={12}>
                                    <Field name={`trueUpType`}>
                                      {({ field }) => {
                                        return (
                                          <FormControl component="fieldset">
                                            <FormLabel className={classes.emphasis}>
                                              True Up Type
                                            </FormLabel>
                                            <RadioGroup
                                              row
                                              aria-label="trueUpType"
                                              name="trueUpType"
                                              value={values.trueUpType}
                                              onChange={(e, value) => {
                                                setFieldValue('trueUpType', value);
                                                if (value === 'Fixed Price') {
                                                  setFieldValue('trueUpPercentage', 0);
                                                }
                                              }}
                                            >
                                              <FormControlLabel
                                                value="Fixed Price"
                                                control={<Radio />}
                                                label="Fixed Price"
                                              />
                                              <FormControlLabel
                                                value="Percent"
                                                control={<Radio />}
                                                label="Percent"
                                              />
                                            </RadioGroup>
                                          </FormControl>
                                        );
                                      }}
                                    </Field>
                                  </Grid>
                                  {values.trueUpType === 'Percent' && (
                                    <Grid item xs={12}>
                                      <TextField
                                        fullWidth
                                        variant="outlined"
                                        autoComplete="nope"
                                        label="True Up Percentage"
                                        name="trueUpPercentage"
                                        size="small"
                                        value={values.trueUpPercentage}
                                        onBlur={e => {
                                          handleBlur(e);
                                          if (e.target.value) {
                                            setFieldValue('trueUpPercentage', `${e.target.value}%`);
                                          }
                                        }}
                                        onChange={e => {
                                          // if value is not blank, then test the regex
                                          if (
                                            e.target.value === '' ||
                                            decimalRegExp.test(e.target.value)
                                          ) {
                                            setFieldValue('trueUpPercentage', e.target.value);
                                          }
                                        }}
                                      />
                                    </Grid>
                                  )}
                                </>
                              )}
                            </>
                          )}
                          <Grid item xs={12} sm={12}>
                            <Field name={`titleTransferFees`}>
                              {({ field }) => {
                                return (
                                  <TextField
                                    fullWidth
                                    required
                                    variant="outlined"
                                    autoComplete="nope"
                                    label="Title Transfer Fees"
                                    name="titleTransferFees"
                                    size="small"
                                    value={values.titleTransferFees}
                                    onBlur={e => {
                                      handleBlur(e);
                                      setFieldValue(
                                        'titleTransferFees',
                                        formatMoney(e.target.value, 2)
                                      );
                                    }}
                                    onChange={e =>
                                      setFieldValue('titleTransferFees', e.target.value)
                                    }
                                    error={Boolean(
                                      touched.titleTransferFees && errors.titleTransferFees
                                    )}
                                    helperText={
                                      touched.titleTransferFees && errors.titleTransferFees
                                    }
                                  />
                                );
                              }}
                            </Field>
                          </Grid>
                          <Grid item xs={12} sm={12}>
                            <TextField
                              fullWidth
                              variant="outlined"
                              autoComplete="nope"
                              label="Plat Book"
                              name="platBook"
                              size="small"
                              InputLabelProps={{ shrink: values.platBook ? true : false }}
                              value={values.platBook}
                              onBlur={handleBlur}
                              onChange={e => setFieldValue('platBook', e.target.value)}
                              error={Boolean(touched.platBook && errors.platBook)}
                              helperText={touched.platBook && errors.platBook}
                            />
                          </Grid>
                          <Grid item xs={12} sm={12}>
                            <Field name={`page`}>
                              {({ field }) => {
                                return (
                                  <TextField
                                    fullWidth
                                    variant="outlined"
                                    autoComplete="nope"
                                    label="Page"
                                    name="page"
                                    size="small"
                                    type="number"
                                    value={values.page}
                                    onBlur={handleBlur}
                                    onChange={e => setFieldValue('page', e.target.value)}
                                    error={Boolean(touched.page && errors.page)}
                                    helperText={touched.page && errors.page}
                                  />
                                );
                              }}
                            </Field>
                          </Grid>
                          <Grid item xs={12} sm={12}>
                            <SMBSelect
                              required
                              onChange={e => {
                                setFieldValue('sitePrepBudgetLevel', e.target.value);
                              }}
                              variant="outlined"
                              handleClear={() => setFieldValue('sitePrepBudgetLevel', '')}
                              name={`sitePrepBudgetLevel`}
                              label={`Site Prep Budget Level`}
                              id={`sitePrepBudgetLevel`}
                              value={values.sitePrepBudgetLevel}
                              onBlur={handleBlur}
                              disabled={isLoadingSitePrepBudgets}
                              error={errors && errors.sitePrepBudgetLevel ? true : false}
                            >
                              {sitePrepBudgets &&
                                sitePrepBudgets.map((sitePrepBudget: any, index) => {
                                  return (
                                    <MenuItem key={`${index + 1}`} value={sitePrepBudget.value}>
                                      {sitePrepBudget.description}
                                    </MenuItem>
                                  );
                                })}
                            </SMBSelect>
                          </Grid>
                          <Grid item xs={12} sm={12}>
                            <Typography>Build Type</Typography>
                            <Typography variant="caption">
                              NOTE: Build Type is needed for the sales web site to know how to show
                              the plans
                            </Typography>
                            <FormGroup>
                              {!isLoadingBuildTypes &&
                                buildTypes &&
                                buildTypes.map(buildType => {
                                  return (
                                    <FormControlLabel
                                      key={buildType.description}
                                      control={
                                        <Field
                                          name={buildType.description}
                                          type={'checkbox'}
                                          value={buildType.value}
                                          props
                                        >
                                          {({ field }) => {
                                            return (
                                              <Checkbox
                                                {...field}
                                                checked={values.buildTypes.includes(
                                                  buildType.value
                                                )}
                                                onChange={() => {
                                                  values.buildTypes.includes(buildType.value)
                                                    ? setFieldValue(
                                                        'buildTypes',
                                                        values.buildTypes.filter(
                                                          val => val !== buildType.value
                                                        )
                                                      )
                                                    : setFieldValue('buildTypes', [
                                                        ...values.buildTypes,
                                                        buildType.value
                                                      ]);
                                                }}
                                                color="primary"
                                              />
                                            );
                                          }}
                                        </Field>
                                      }
                                      label={buildType.description}
                                    />
                                  );
                                })}
                            </FormGroup>
                          </Grid>
                          <Grid item xs={12} sm={12}>
                            <Field name={`startsPerWeek`}>
                              {({ field }) => {
                                return (
                                  <TextField
                                    fullWidth
                                    variant="outlined"
                                    autoComplete="nope"
                                    label="Starts Per Week"
                                    name="startsPerWeek"
                                    size="small"
                                    value={values.startsPerWeek}
                                    onBlur={handleBlur}
                                    onChange={e => setFieldValue('startsPerWeek', e.target.value)}
                                    error={Boolean(touched.startsPerWeek && errors.startsPerWeek)}
                                    helperText={
                                      (touched.startsPerWeek && errors.startsPerWeek) ||
                                      'Leave blank to only look at territory cap'
                                    }
                                  />
                                );
                              }}
                            </Field>
                          </Grid>
                          <Grid item xs={12} sm={12}>
                            <Field name={`documentationTimeInDays`}>
                              {({ field }) => {
                                return (
                                  <TextField
                                    fullWidth
                                    variant="outlined"
                                    autoComplete="nope"
                                    label="Documentation Time In Days"
                                    name="documentationTimeinDays"
                                    size="small"
                                    InputLabelProps={{
                                      shrink: values.documentationTimeInDays ? true : false
                                    }}
                                    value={values.documentationTimeInDays}
                                    onBlur={handleBlur}
                                    onChange={e =>
                                      setFieldValue('documentationTimeInDays', e.target.value)
                                    }
                                    error={Boolean(
                                      touched.documentationTimeInDays &&
                                        errors.documentationTimeInDays
                                    )}
                                    helperText={
                                      (touched.documentationTimeInDays &&
                                        errors.documentationTimeInDays) ||
                                      'Time from Contract to Form'
                                    }
                                  />
                                );
                              }}
                            </Field>
                          </Grid>
                          <Grid item xs={12} sm={12}>
                            <Field name={`buildTimeInDays`}>
                              {({ field }) => {
                                return (
                                  <TextField
                                    fullWidth
                                    variant="outlined"
                                    autoComplete="nope"
                                    label="Neighborhood Build Time In Days"
                                    name="buildTimeInDays"
                                    size="small"
                                    InputLabelProps={{
                                      shrink: values.buildTimeInDays ? true : false
                                    }}
                                    value={values.buildTimeInDays}
                                    onBlur={handleBlur}
                                    onChange={e => setFieldValue('buildTimeInDays', e.target.value)}
                                    error={Boolean(
                                      touched.buildTimeInDays && errors.buildTimeInDays
                                    )}
                                    helperText={
                                      (touched.buildTimeInDays && errors.buildTimeInDays) ||
                                      'Time from Frame to Complete'
                                    }
                                  />
                                );
                              }}
                            </Field>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Box>
                  <Box mt={1} mb={0.5}>
                    <Divider />
                  </Box>
                  <ButtonGroup>
                    <Button
                      disabled={!dirty || isSubmitting || !isValid}
                      type="submit"
                      startIcon={neighborhoodId ? <Save /> : <Add />}
                      variant="contained"
                      color="primary"
                    >
                      Save
                    </Button>
                    <Button
                      type="button"
                      variant="contained"
                      color="inherit"
                      startIcon={<Close />}
                      onClick={() => {
                        history.push('/development/neighborhoods');
                      }}
                    >
                      Cancel
                    </Button>
                  </ButtonGroup>
                </Form>
              </>
            );
          }}
        </Formik>
      )}
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  emphasis: {
    color: theme.palette.primary.main,
    fontWeight: 600
  }
}));
